/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Prompt Templates Catalog & Explorer Zustand Store (Task 10.2.5)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type PromptCatalogTab = 'all' | 'favorites' | 'recent';
export type ViewMode = 'grid' | 'compact';

export interface PromptTemplateStoreState {
  searchQuery: string;
  selectedCategory: string; // 'all' or category slug
  selectedModel: string; // 'all' or model code
  activeTab: PromptCatalogTab;
  favorites: string[]; // List of template slugs
  recentlyUsed: string[]; // List of template slugs, sorted by recency
  selectedTemplateSlug: string | null;
  viewMode: ViewMode;

  // Actions
  setSearchQuery: (query: string) => void;
  setSelectedCategory: (categorySlug: string) => void;
  setSelectedModel: (modelCode: string) => void;
  setActiveTab: (tab: PromptCatalogTab) => void;
  toggleFavorite: (slug: string) => void;
  isFavorite: (slug: string) => boolean;
  markRecentlyUsed: (slug: string) => void;
  setSelectedTemplate: (slug: string | null) => void;
  setViewMode: (mode: ViewMode) => void;
  resetFilters: () => void;
}

// Fallback storage for SSR or test environments
const dummyStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

export const usePromptTemplateStore = create<PromptTemplateStoreState>()(
  persist(
    (set, get) => ({
      searchQuery: '',
      selectedCategory: 'all',
      selectedModel: 'all',
      activeTab: 'all',
      favorites: [],
      recentlyUsed: [],
      selectedTemplateSlug: null,
      viewMode: 'grid',

      setSearchQuery: (searchQuery) => set({ searchQuery }),

      setSelectedCategory: (selectedCategory) =>
        set({ selectedCategory, activeTab: 'all' }),

      setSelectedModel: (selectedModel) => set({ selectedModel }),

      setActiveTab: (activeTab) => set({ activeTab }),

      toggleFavorite: (slug) => {
        const current = get().favorites;
        const exists = current.includes(slug);
        const next = exists
          ? current.filter((s) => s !== slug)
          : [slug, ...current];
        set({ favorites: next });
      },

      isFavorite: (slug) => {
        return get().favorites.includes(slug);
      },

      markRecentlyUsed: (slug) => {
        const current = get().recentlyUsed;
        const filtered = current.filter((s) => s !== slug);
        // Put latest used at the front, keep up to 20 items
        set({ recentlyUsed: [slug, ...filtered].slice(0, 20) });
      },

      setSelectedTemplate: (selectedTemplateSlug) =>
        set({ selectedTemplateSlug }),

      setViewMode: (viewMode) => set({ viewMode }),

      resetFilters: () =>
        set({
          searchQuery: '',
          selectedCategory: 'all',
          selectedModel: 'all',
          activeTab: 'all',
        }),
    }),
    {
      name: 'sofia_prompt_templates_v2',
      storage: createJSONStorage(() =>
        typeof window !== 'undefined' ? window.localStorage : dummyStorage
      ),
      partialize: (state) => ({
        favorites: state.favorites,
        recentlyUsed: state.recentlyUsed,
        viewMode: state.viewMode,
      }),
    }
  )
);
